import { emptyTally, NUTRIENT_DISPLAY_ORDER, type NutritionTally } from 'pages/FarmsPage/Workspace/Insights/computeLiveTotals';
import type {
  AreaWeightedBalance,
  NutritionAudit,
  NutritionPlanAudit,
  PaddockNutritionAudit,
} from './computeNutritionAudit';

/** One plan in the crop grouping — a row labelled by its paddock, since the group names the crop. */
export interface CropPlanRow {
  /** The plan's inputId — a plan linked to two paddocks is one row. */
  key: string;
  plan: NutritionPlanAudit;
  season: number;
  /** The first paddock the plan is on, for the row's link. */
  paddockId: string;
  /** Every paddock the plan is on, e.g. "North, South". */
  paddockName: string;
  farmName: string;
  /** Applied − removed for the plan, kg/ha of its own area. */
  balance: NutritionTally;
}

export interface CropPlanGroup extends AreaWeightedBalance {
  cropName: string;
  rows: CropPlanRow[];
}

export interface CropPlanGrouping {
  groups: CropPlanGroup[];
  total: AreaWeightedBalance;
}

const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Each row's balance weighted by its plan's area, kg/ha. */
const areaWeighted = (rows: CropPlanRow[]): AreaWeightedBalance => {
  const balance = emptyTally();
  let areaHa = 0;
  for (const row of rows) {
    if (row.plan.areaHa <= 0) continue;
    areaHa += row.plan.areaHa;
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      balance[key] += row.balance[key] * row.plan.areaHa;
    }
  }
  if (areaHa > 0) {
    for (const key of NUTRIENT_DISPLAY_ORDER) balance[key] /= areaHa;
  }
  return { areaHa, balance };
};

/**
 * The audit's plans grouped by crop, across every year in range, so an
 * advisor can work on, say, every canola plan at once. Each plan is one row
 * with its own balance (applied − removed, kg/ha of its area); groups are in
 * crop name order, rows by year then paddock. Each group, and the whole
 * scope, has an area-weighted total over its plans.
 *
 * Pure — regroups what computeNutritionAudit already worked out.
 */
export const groupPlansByCrop = (audit: NutritionAudit): CropPlanGrouping => {
  const rowsByPlan = new Map<string, CropPlanRow>();
  const paddocks: PaddockNutritionAudit[] = audit.farms.flatMap(
    (farm) => farm.paddocks
  );
  for (const paddock of paddocks) {
    for (const season of paddock.seasons) {
      for (const plan of season.plans) {
        const existing = rowsByPlan.get(plan.inputId);
        if (existing) {
          existing.paddockName = [existing.paddockName, paddock.paddockName].join(
            ', '
          );
          continue;
        }
        const balance = emptyTally();
        for (const key of NUTRIENT_DISPLAY_ORDER) {
          balance[key] = plan.applied[key] - plan.removed[key];
        }
        rowsByPlan.set(plan.inputId, {
          key: plan.inputId,
          plan,
          season: season.season,
          paddockId: paddock.paddockId,
          paddockName: paddock.paddockName,
          farmName: paddock.farmName,
          balance,
        });
      }
    }
  }

  const rows = Array.from(rowsByPlan.values());
  const byCrop = new Map<string, CropPlanRow[]>();
  for (const row of rows) {
    const crop = row.plan.cropName;
    byCrop.set(crop, [...(byCrop.get(crop) ?? []), row]);
  }

  const groups = Array.from(byCrop.entries())
    .map(([cropName, cropRows]) => ({
      cropName,
      rows: [...cropRows].sort(
        (a, b) => a.season - b.season || byName(a.paddockName, b.paddockName)
      ),
      ...areaWeighted(cropRows),
    }))
    .sort((a, b) => byName(a.cropName, b.cropName));

  return { groups, total: areaWeighted(rows) };
};
